import api from "@/lib/api";

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// Downscale large phone photos before upload (max 1600px, JPEG ~0.82).
async function downscaleImage(file) {
  if (!file.type.startsWith("image/") || file.type === "image/gif") return file;
  try {
    const bitmap = await createImageBitmap(file);
    const MAX = 1600;
    const { width, height } = bitmap;
    if (width <= MAX && height <= MAX && file.size <= 1024 * 1024) { bitmap.close?.(); return file; }
    const scale = Math.min(1, MAX / Math.max(width, height));
    const w = Math.round(width * scale);
    const h = Math.round(height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.82));
    if (!blob || blob.size >= file.size) return file;
    const name = file.name.replace(/\.(png|jpe?g|webp|heic|heif|bmp|gif)$/i, "") + ".jpg";
    return new File([blob], name, { type: "image/jpeg" });
  } catch (e) {
    return file;
  }
}

const newUploadId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(36).slice(2)}`;

// Sends a file in 512KB base64 chunks, 4 at a time, retrying each chunk up to 3 times.
async function sendChunks(file, url, extra, uploadId, onProgress) {
  const CHUNK = 512 * 1024;
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  let next = 0, done = 0;
  const one = async (i) => {
    const data = await blobToBase64(file.slice(i * CHUNK, (i + 1) * CHUNK));
    for (let a = 0; ; a++) {
      try {
        await api.post(url, { ...extra, upload_id: uploadId, index: i, total, data, filename: file.name || "file", content_type: file.type || "application/octet-stream" });
        break;
      } catch (e) {
        if (a >= 2 || (e.response && e.response.status < 500 && e.response.status !== 429)) throw e;
        await new Promise((r) => setTimeout(r, 800 * (a + 1)));
      }
    }
    done += 1;
    if (onProgress) onProgress(Math.round((done / total) * 100));
  };
  const worker = async () => { while (next < total) await one(next++); };
  await Promise.all(Array.from({ length: Math.min(4, total) }, worker));
}

// Uploads a File in ~512KB chunks (base64) to stay under ingress body limits,
// then finalizes it as a document attached to the given load.
export async function uploadFileChunked(file, loadId, docType, onProgress) {
  const prepared = await downscaleImage(file);
  const uploadId = newUploadId();
  await sendChunks(prepared, "/uploads/chunk", {}, uploadId, onProgress);
  const { data } = await api.post(`/loads/${loadId}/documents`, { upload_id: uploadId, doc_type: docType });
  return data;
}

// Prefix a backend-relative asset/media path with the API host for use in <img src>.
export function mediaUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//.test(path) || path.startsWith("data:")) return path;
  return `${process.env.REACT_APP_BACKEND_URL}${path}`;
}

// Uploads any File as a generic asset (avatars, chat media) and returns {id, url, ...}.
export async function uploadAsset(file, category = "misc", onProgress) {
  const prepared = await downscaleImage(file);
  const uploadId = newUploadId();
  await sendChunks(prepared, "/uploads/chunk", {}, uploadId, onProgress);
  const { data } = await api.post("/uploads/finalize", { upload_id: uploadId, category });
  return data;
}

// Token-gated public upload (carrier / HR onboarding) — no auth required.
export async function uploadPublicAsset(file, token, category = "onboarding", onProgress) {
  if (file.size > 25 * 1024 * 1024) throw new Error("File too large (max 25MB)");
  const prepared = await downscaleImage(file);
  const uploadId = newUploadId();
  await sendChunks(prepared, "/public-uploads/chunk", { token }, uploadId, onProgress);
  const { data } = await api.post("/public-uploads/finalize", { token, upload_id: uploadId, category });
  return data;
}
